'use client';

import {
	BigNum,
	MarketType,
	PerpMarketAccount,
	PRICE_PRECISION_EXP,
	SpotMarketAccount,
} from '@velocity-exchange/sdk';
import {
	MarketId,
	UIMarket,
	getPausedOperations,
} from '@velocity-exchange/common';
import { memo, useMemo, useRef } from 'react';
import useCurrentMarketMarkPrice from 'src/hooks/useCurrentMarketMarkPrice';
import useInfoForCurrentlySelectedMarket from '../../hooks/useInfoForCurrentlySelectedMarket';
import useMarketInfoDisplayData from '../../hooks/useMarketInfoDisplayData';
import useDriftStore from '../../stores/DriftStore/useDriftStore';
import { Rolling24HrVolumeNumberDisplay } from './Rolling24hrVolumeNumberDisplay';
import StatDisplay from './StatDisplay';
import { ViewDetailsForMarketButton } from './ViewDetailsForMarket';
import SkeletonValuePlaceholder from '../SkeletonValuePlaceholder/SkeletonValuePlaceholder';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import { getIsPreLaunchMarket } from '../PreLaunchMarketAlerts/PreLaunchMarketAlertUtils';
import { getMarketPriceDecimals } from 'src/constants/marketDecimalConfig';
import PreLaunchMarketIcon from '../PreLaunchMarketAlerts/PreLaunchMarketIcon';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { MarketOracleInfo } from '../MarketInfo/MarketOracleInfo';
import NumberDisplay from '../NumberDisplay/NumberDisplay';
import { cj } from 'src/utils/classNames';
import {
	MARKET_STATS_BAR_CELL_LABEL_CLASSNAME,
	MARKET_STATS_BAR_CELL_CLASSNAME,
	MARKET_STATS_BAR_CELL_VALUE_SKELETON_CLASSNAME,
} from 'src/components/MarketStatsBar/consts';
import { getColorVar } from 'src/utils/theme/colorVars';
import FundingRateCard from 'src/components/MarketStatsBar/FundingRateCard';
import OpenInterestCard from 'src/components/MarketStatsBar/OpenInterestCard';
import MarkPriceDisplay from 'src/components/MarketStatsBar/MarkPriceDisplay';
import ScrollShadows from 'src/components/ScrollShadows';

function MarketStatsBar() {
	const isMobileScreenSize = useIsMobileScreenSize();
	const scrollContainerRef = useRef<HTMLDivElement>(null);

	const selectedMarketId = useDriftStore(
		(s) => s.selectedMarket.current?.marketId
	);
	const selectedMarketBaseAssetSymbol = useDriftStore(
		(s) => s.selectedMarket.current?.uiSymbols.baseAssetSymbol
	);

	const selectedMarketInfo = useInfoForCurrentlySelectedMarket();

	const marketData = useMarketInfoDisplayData();

	const markPrice = useCurrentMarketMarkPrice();

	const marketAccount = selectedMarketInfo?.info?.account as
		| PerpMarketAccount
		| SpotMarketAccount;

	const isPreLaunchMarket = getIsPreLaunchMarket(
		selectedMarketId as MarketId,
		marketAccount
	);

	const pausedOperations = useMemo(
		() => (marketAccount ? getPausedOperations(marketAccount) : []),
		[marketAccount]
	);

	const showOraclePrice =
		marketData.hasLoadedInitialData &&
		selectedMarketInfo.info &&
		marketData?.oraclePrice !== undefined;

	const oracleSource = selectedMarketId?.isPerp
		? (marketAccount as PerpMarketAccount)?.oracleSource
		: (marketAccount as SpotMarketAccount)?.oracleSource;
	const oracle = selectedMarketId?.isPerp
		? (marketAccount as PerpMarketAccount)?.oracle
		: (marketAccount as SpotMarketAccount)?.oracle;

	return (
		<ScrollShadows
			className={cj(
				'relative flex size-full items-center justify-center',
				'min-w-0 flex-1 bg-container-bg',
				isMobileScreenSize && 'px-xs pb-md'
			)}
			gradientFromClassName="from-container-bg"
			scrollableRef={scrollContainerRef}
			screenSize="all"
		>
			<div
				className="hidden-scroll flex w-full items-start justify-between overflow-x-auto overflow-y-hidden whitespace-nowrap sm:items-stretch sm:justify-start"
				ref={scrollContainerRef}
			>
				{/* Mark price section — large price + 24h % change */}
				{!isMobileScreenSize && (
					<div className="px-2xl">
						<MarkPriceDisplay
							loading={!markPrice || markPrice.eqZero()}
							price={markPrice?.toString()}
							marketId={selectedMarketId as MarketId}
						/>
					</div>
				)}

				<div className={MARKET_STATS_BAR_CELL_CLASSNAME}>
					<Tooltip
						decoration="cursor-help"
						content={
							!isPreLaunchMarket && (
								<MarketOracleInfo
									marketData={marketData}
									oracleSource={oracleSource}
									selectedMarketBaseAssetSymbol={
										selectedMarketBaseAssetSymbol ?? ''
									}
									oracle={oracle}
									tenSlotOracleDelayText={
										selectedMarketId?.isPerp
											? 'The price feed has not updated in the last 10 slots, so AMM might be unavailable for fills.'
											: 'The price feed has not updated in the last 10 slots.'
									}
									confidenceIntervalTooLargeText={
										selectedMarketId?.isPerp
											? 'The price feed confidence interval too large, so AMM might be unavailable for fills.'
											: 'The price feed confidence interval too large.'
									}
								/>
							)
						}
					>
						<div className="flex flex-col">
							<StatDisplay
								header="Oracle"
								headerClassName={MARKET_STATS_BAR_CELL_LABEL_CLASSNAME}
								leftBorder
							>
								<div className="flex flex-col">
									{showOraclePrice ? (
										<div className="flex items-center text-text-default">
											<NumberDisplay
												value={BigNum.fromPrint(
													marketData.mmOraclePrice?.toString() ??
														marketData.oraclePrice.toString(),
													PRICE_PRECISION_EXP
												)}
												type="currency"
												decimals={getMarketPriceDecimals(
													selectedMarketBaseAssetSymbol || ''
												)}
											/>
											{!isMobileScreenSize && isPreLaunchMarket && (
												<div className="h-3 w-6">
													<PreLaunchMarketIcon
														color={getColorVar('text-secondary')}
														className="relative top-[-3px] ml-xs"
													/>
												</div>
											)}
										</div>
									) : (
										<SkeletonValuePlaceholder
											className={MARKET_STATS_BAR_CELL_VALUE_SKELETON_CLASSNAME}
											loading
										/>
									)}
								</div>
							</StatDisplay>
						</div>
					</Tooltip>
				</div>

				{selectedMarketId?.isPerp &&
					!(isPreLaunchMarket && pausedOperations?.includes('Funding')) && (
						<div className={MARKET_STATS_BAR_CELL_CLASSNAME}>
							<FundingRateCard
								marketSymbol={marketData.symbol}
								selectedMarketId={selectedMarketId as MarketId}
								fundingRateUnit="h"
							/>
						</div>
					)}

				{selectedMarketId?.isPerp && (
					<div className={MARKET_STATS_BAR_CELL_CLASSNAME}>
						<OpenInterestCard
							marketId={selectedMarketId}
							symbol={marketData.symbol}
							maxOpenInterest={marketData.maxOpenInterest}
							longOpenInterest={marketData.longOpenInterest}
							shortOpenInterest={marketData.shortOpenInterest}
							openInterest={marketData.openInterest}
							oraclePrice={marketData.oraclePrice}
						/>
					</div>
				)}

				<div className={MARKET_STATS_BAR_CELL_CLASSNAME}>
					<Rolling24HrVolumeNumberDisplay
						marketIndex={selectedMarketId?.marketIndex ?? 0}
						marketType={selectedMarketId?.marketType ?? MarketType.PERP}
					/>
				</div>

				{!isMobileScreenSize && (
					<div className={MARKET_STATS_BAR_CELL_CLASSNAME}>
						<ViewDetailsForMarketButton
							market={UIMarket.fromMarketId(selectedMarketId as MarketId)}
						/>
					</div>
				)}
			</div>
		</ScrollShadows>
	);
}

export default memo(MarketStatsBar);
